<script setup>
import { computed, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';

import emojiGroups from './emojisGroup.json';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  onClick: {
    type: Function,
    default: () => {},
  },
  showRemoveButton: {
    type: Boolean,
    default: false,
  },
  expandedLayout: {
    type: Boolean,
    default: false,
  },
  /** How the panel is anchored inside its `position: relative` parent */
  placement: {
    type: String,
    default: 'legacy',
    validator: v =>
      ['legacy', 'above-toolbar-start', 'below-anchor-start'].includes(v),
  },
});

const SLUG_ICONS = {
  smileys_emotion: 'i-lucide-smile-plus',
  people_body: 'i-lucide-user',
  animals_nature: 'i-lucide-leaf',
  food_drink: 'i-lucide-utensils',
  travel_places: 'i-lucide-car',
  activities: 'i-lucide-zap',
  objects: 'i-lucide-lightbulb',
  symbols: 'i-lucide-hash',
  flags: 'i-lucide-flag',
};

const { t } = useI18n();

const searchQuery = ref('');
const activeSlug = ref(emojiGroups[0]?.slug ?? '');

const enrichedItems = shallowRef([]);

function buildEnriched() {
  enrichedItems.value = emojiGroups.flatMap(group =>
    group.emojis.map(em => ({
      emoji: em.emoji,
      name: em.name,
      slug: em.slug,
      groupSlug: group.slug,
      groupName: group.name,
      searchString: `${em.name} ${em.slug}`.toLowerCase(),
    }))
  );
}

buildEnriched();

const categoryTabs = computed(() =>
  emojiGroups.map(g => ({
    slug: g.slug,
    icon: SLUG_ICONS[g.slug] || 'i-lucide-smile',
  }))
);

const searchResults = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];
  return enrichedItems.value
    .filter(i => i.searchString.includes(q))
    .slice(0, 216);
});

const activeGroup = computed(() =>
  emojiGroups.find(g => g.slug === activeSlug.value)
);

const hasSearchQuery = computed(() => searchQuery.value.trim().length > 0);

function selectEmoji(character) {
  props.onClick(character);
}

function setCategory(slug) {
  activeSlug.value = slug;
  searchQuery.value = '';
}

function removeEmoji() {
  props.onClick('');
}

const positionClass = computed(() => {
  if (props.placement === 'above-toolbar-start') {
    return 'absolute z-[110] bottom-full mb-1 start-0';
  }
  if (props.placement === 'below-anchor-start') {
    return 'absolute z-[110] top-full mt-1.5 start-0';
  }
  if (props.expandedLayout) {
    return 'absolute bottom-0 z-[100] ltr:left-1 rtl:right-1 ltr:right-[unset] rtl:left-[unset]';
  }
  return 'absolute z-[110] ltr:right-0 rtl:left-0 -top-[95px]';
});
</script>

<template>
  <div
    role="dialog"
    class="emoji-dialog flex max-h-[min(72vh,21rem)] w-[min(calc(100vw-2rem),22rem)] flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-xl"
    :class="positionClass"
  >
    <div
      class="flex shrink-0 gap-0.5 overflow-x-auto border-b border-n-weak px-2 pt-2"
      role="tablist"
    >
      <button
        v-for="tab in categoryTabs"
        :key="tab.slug"
        type="button"
        role="tab"
        class="flex shrink-0 flex-col items-center px-2 pb-2 transition-colors"
        :class="
          activeSlug === tab.slug && !searchQuery.trim()
            ? 'border-b-2 border-n-brand text-n-brand'
            : 'border-b-2 border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        :aria-selected="activeSlug === tab.slug && !searchQuery.trim()"
        @click="setCategory(tab.slug)"
      >
        <Icon :icon="tab.icon" class="size-5" />
      </button>
    </div>

    <div class="flex shrink-0 gap-2 border-b border-n-weak px-2 py-2">
      <Input
        v-model="searchQuery"
        type="search"
        size="sm"
        class="group min-w-0 flex-1 [&>input]:rounded-lg [&>input]:ltr:!pl-9 [&>input]:rtl:!pr-9"
        :placeholder="t('EMOJI.PLACEHOLDER')"
      />
      <NextButton
        v-if="showRemoveButton"
        faded
        sm
        slate
        class="shrink-0"
        :label="t('EMOJI.REMOVE')"
        @click="removeEmoji"
      />
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-2">
      <template v-if="hasSearchQuery">
        <template v-if="searchResults.length">
          <p
            class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('EMOJI.SEARCH_RESULTS') }}
          </p>
          <div class="grid grid-cols-9 gap-1">
            <button
              v-for="(item, idx) in searchResults"
              :key="`${item.slug}-${idx}`"
              type="button"
              class="flex size-10 items-center justify-center rounded-lg text-2xl leading-none hover:bg-n-alpha-2"
              :title="item.name"
              @click="selectEmoji(item.emoji)"
            >
              {{ item.emoji }}
            </button>
          </div>
        </template>
        <p v-else class="mb-0 text-sm text-n-slate-11">
          {{ t('EMOJI.NOT_FOUND') }}
        </p>
      </template>

      <template v-else-if="activeGroup">
        <p
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ activeGroup.name }}
        </p>
        <div class="grid grid-cols-9 gap-1">
          <button
            v-for="(item, idx) in activeGroup.emojis"
            :key="`${item.slug}-${idx}`"
            type="button"
            class="flex size-10 items-center justify-center rounded-lg text-2xl leading-none hover:bg-n-alpha-2"
            :title="item.name"
            @click="selectEmoji(item.emoji)"
          >
            {{ item.emoji }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
